import { Form, Input, Select, Tooltip } from 'antd';
import {
	IMobileRef,
	IPOAccount,
	IPOButton,
	IPOMessage,
	IPOModal,
	IPOUtils,
	MobileInput,
} from '../../..';
import React, { FC, useRef, ReactNode, useState, useEffect } from 'react';
import { EditUserWrap, FooterWrap } from './style';

interface IStaffEditProps {
	isShowEdit: boolean;
	user: IPOAccount.IStaff;
	handleUpdateBasic: (staff: IPOAccount.IEditAccount) => Promise<void>;
	handleUpdatePwd: (pwd: string) => Promise<void>;
	handleUpdateRole?: (role: IPOAccount.StaffRoleEnum) => Promise<void>;
	handleDisabled: () => Promise<void>;
	handleChangeStatus: () => void;
	showRight?: boolean;
	uploadEle: ReactNode;
	isDisabledRight?: boolean;
}

export const EditStaff: FC<IStaffEditProps> = ({
	isShowEdit,
	user,
	showRight,
	handleDisabled,
	handleChangeStatus,
	handleUpdateBasic,
	handleUpdatePwd,
	handleUpdateRole,
	uploadEle,
	isDisabledRight,
}) => {
	showRight = showRight ?? false;
	isDisabledRight = isDisabledRight ?? false;

	const { Option } = Select;
	const mobileRef = useRef<IMobileRef>();
	const [detailForm] = Form.useForm();
	const [passwordForm] = Form.useForm();

	const [activeKey, setActiveKey] = useState<'basicInfo' | 'password'>('basicInfo');
	const [copyAvatar, setCopyAvatar] = useState<string | undefined>('');
	const [delVisible, setDelVisible] = useState(false);

	useEffect(() => {
		if (!isShowEdit) {
			detailForm.resetFields();
			passwordForm.resetFields();
			setActiveKey('basicInfo');
		} else {
			detailForm.setFieldsValue({ ...user });
			setCopyAvatar(user.avatar);
		}
	}, [isShowEdit]);

	const handleSure: () => void = () => {
		if (activeKey === 'password') {
			// 更新密码
			const pwd = passwordForm.getFieldValue('newPwd').trim();
			const pwd2 = passwordForm.getFieldValue('newPwd2').trim();
			if (pwd !== pwd2) {
				return IPOMessage.error('两次密码不一致');
			}
			handleUpdatePwd(passwordForm.getFieldValue('newPwd')).then(() => {
				return IPOMessage.success('密码修改成功');
			});
			handleChangeStatus();
		} else {
			const email = detailForm.getFieldValue('email')?.trim() ?? '';
			const name = detailForm.getFieldValue('name')?.trim() ?? '';
			const name2 = detailForm.getFieldValue('name2')?.trim() ?? '';
			const avatar = user.avatar ?? '';

			// 更新基本信息
			IPOUtils.regAccount({ email, name, name2, avatar }, false).then(() => {
				const isSameBasic =
					user.name === name &&
					user.name2 === name2 &&
					user.email === email &&
					avatar === copyAvatar;
				const isSameRole = handleUpdateRole && user.role == detailForm.getFieldValue('role');
				const newPro = [];
				!isSameBasic && newPro.push(handleUpdateBasic({ name, name2, email, avatar }));
				!isSameRole &&
					newPro.push(handleUpdateRole && handleUpdateRole(detailForm.getFieldValue('role')));
				Promise.all(newPro).then(() => {
					return IPOMessage.success('保存成功');
				});
				handleChangeStatus();
			});
		}
	};

	const handleDis: () => void = () => {
		setDelVisible(true);
	}

	const getBasicInfoEle: () => ReactNode = () => {
		return (
			<Form colon={false} labelCol={{ span: 3, offset: 0 }} form={detailForm}>
				<Form.Item label="头像" name="avatar">
					{uploadEle}
				</Form.Item>
				<Form.Item label="英文名" name="name">
					<Input />
				</Form.Item>
				<Form.Item label="中文名" name="name2">
					<Input />
				</Form.Item>
				<Form.Item label="手机号" rules={[{ required: true, message: '请输入手机号' }]}>
					<MobileInput
						disabled
						allowClear={false}
						mobileRef={mobileRef}
						mobileInfo={{ areaCode: user.areaCode, phoneNum: user.mobile }}
					/>
				</Form.Item>
				<Form.Item label="邮箱" name="email">
					<Input />
				</Form.Item>
				{showRight && (
					<Form.Item
						label="权限"
						name="role"
						rules={[{ required: true, message: 'Please input your username!' }]}>
						<Select
							value={detailForm.getFieldValue('role')}
							style={{ width: '100%' }}
							onChange={() => {}}>
							<Option value={IPOAccount.StaffRoleEnum.CUSTOM}>普通权限</Option>
							<Option value={IPOAccount.StaffRoleEnum.HIGH_LEVEL}>高级权限</Option>
							<Option value={IPOAccount.StaffRoleEnum.READ_ONLY}>监管方权限</Option>
						</Select>
					</Form.Item>
				)}
			</Form>
		);
	};

	const getPasswordEle: () => ReactNode = () => {
		return (
			<Form colon={false} labelCol={{ span: 3, offset: 0 }} form={passwordForm}>
				<Form.Item label="新密码" name="newPwd">
					<Input.Password />
				</Form.Item>
				<Form.Item label="确认密码" name="newPwd2">
					<Input.Password />
				</Form.Item>
			</Form>
		);
	};

	return (
		<EditUserWrap>
			<IPOModal
				title="修改信息"
				isShowPadding
				visible={isShowEdit}
				getContainer={false}
				onCancel={() => handleChangeStatus()}
				maskClosable={false}
				wrapClassName='modal-wrap'
				footer={
					<FooterWrap>
						<div className="left">
							{activeKey === 'basicInfo' && !isDisabledRight && (
								<Tooltip title="可禁用该员工，禁用后该员工不可再登录此后台管理系统。" mouseEnterDelay={2} mouseLeaveDelay={0.5}>
									<IPOButton
										type="dashed"
										onClick={() => handleDis()}>
										禁用
									</IPOButton>
								</Tooltip>
							)}
						</div>
						<div className="right">
							<IPOButton type="link" onClick={() => handleChangeStatus()}>
								取消
							</IPOButton>
							<IPOButton type="primary" onClick={() => handleSure()}>
								确定
							</IPOButton>
						</div>
					</FooterWrap>
				}>
				<div className="form-title--wrap">
					<span
						className={`title-name ${activeKey === 'basicInfo' ? 'active' : ''}`}
						onClick={() => setActiveKey('basicInfo')}>
						基本信息
					</span>
					<span
						className={`title-name ${activeKey === 'password' ? 'active' : ''}`}
						onClick={() => setActiveKey('password')}>
						重置密码
					</span>
				</div>
				{activeKey === 'basicInfo' && getBasicInfoEle()}
				{activeKey === 'password' && getPasswordEle()}
				{delVisible && <IPOModal
					wrapClassName='disabled-modal'
					maskClosable={false}
					getContainer={false}
					isShowPadding={true}
					title={'禁用'}
					visible={delVisible}
					okText={'确定'}
					cancelText={'取消'}
					bodyStyle={{
						minHeight: '40px',
					}}
					onOk={() => {
						setDelVisible(false);
						handleDisabled().then(() => {
							handleChangeStatus()
						})
					}}
					onCancel={() => { setDelVisible(false) }}>
					<div>
						禁用后该员工不可登录该系统
					</div>
				</IPOModal>}
			</IPOModal>
		</EditUserWrap>
	);
};
